import { useQuery } from '@tanstack/react-query';

import { fetchDevices } from '@/entities/device/api';
import { isCameraLike, type DeviceSummary } from '@/entities/device/types';
import { useAuthStore } from '@/entities/session/auth-store';

/**
 * Что умеет оборудование компании — и, значит, какие разделы кабинета имеют смысл.
 *
 * Кабинет вырос из учёта рабочего времени: главная, журнал проходов, сотрудники,
 * посещаемость и график стоят на терминалах FaceID. Компании, у которой стоят только
 * камеры (или видеорегистратор), этим разделам показать нечего — пустой журнал
 * проходов и отчёт по явке из одних нулей читаются как поломка. Владелец (2026-10-22):
 * «если там только камеры — пусть будут только отделы, устройства и настройки».
 *
 * Поэтому навигация спрашивает не «какие устройства», а «какие ВОЗМОЖНОСТИ они дают»:
 *
 *   attendance — проходы и явка: терминал FaceID, а также турникет, контроллер и всё,
 *                чего кабинет не знает (неизвестный вид — скорее дверь, чем камера);
 *   video      — живая картинка: камера, PTZ, видеорегистратор.
 *
 * Новый вид устройства добавляет сюда строку, а не ветку в каждом экране.
 *
 * Пустая компания считается умеющей всё: её первый шаг — подключить терминал, и
 * прятать разделы до первого устройства значило бы прятать сам продукт.
 */
export type FleetFeatures = {
  attendance: boolean;
  video: boolean;
};

export const ALL_FEATURES: FleetFeatures = { attendance: true, video: true };

/**
 * Возможности по строкам списка устройств. `complete: false` — список пришёл не
 * целиком (страница меньше `meta.total`): невидимый остаток может оказаться
 * терминалом, поэтому учёт явки тогда не прячется.
 */
export function fleetFeatures(
  rows: readonly Pick<DeviceSummary, 'device_type'>[],
  complete: boolean,
): FleetFeatures {
  if (rows.length === 0) {
    return ALL_FEATURES;
  }
  const video = rows.some(r => isCameraLike(r.device_type));
  const attendance = !complete || rows.some(r => !isCameraLike(r.device_type));
  return { attendance, video };
}

/** Столько строк хватает, чтобы ответить без второго запроса почти любой компании. */
const PROBE_SIZE = 100;

/**
 * Возможности оборудования активной компании.
 *
 * `ready: false`, пока список не пришёл в первый раз: навигатор ждёт его, а не рисует
 * полный набор вкладок, чтобы через секунду его урезать. Ошибка — тоже ответ: тогда
 * показывается всё, потому что спрятать раздел из-за сбоя сети хуже, чем показать
 * лишний.
 *
 * Ключ начинается с `devices, companyId`, так что подключение и удаление устройства,
 * которые сбрасывают `['devices']` или `['devices', companyId]`, пересчитывают и
 * набор разделов.
 */
export function useFleetFeatures(): {
  features: FleetFeatures;
  ready: boolean;
} {
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const query = useQuery({
    queryKey: ['devices', companyId, 'fleet'],
    queryFn: () => fetchDevices(companyId as string, { perPage: PROBE_SIZE }),
    enabled: !!companyId,
    staleTime: 60_000,
  });

  if (!companyId) {
    return { features: ALL_FEATURES, ready: true };
  }
  if (query.isPending) {
    return { features: ALL_FEATURES, ready: false };
  }
  if (query.isError || !query.data) {
    return { features: ALL_FEATURES, ready: true };
  }
  const rows = query.data.data ?? [];
  const total = query.data.meta?.total ?? rows.length;
  return { features: fleetFeatures(rows, rows.length >= total), ready: true };
}
